import { getMonitorStatusOutlineColorHex, MonitorStatus } from '@fe/core';
import { Box, Icon, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useEffect, useMemo, useState } from 'react';
import { FlightListFilterMode, FlightStatus } from '../../../common/enums';
import { queryFlightsGrid } from '../../../services/grid-query-service';
import { UDViewTagSelectionEnum } from '../../../udView/udview';
import { useAppSelector } from '../../hooks/store-hooks';
import { dashboardCategoryIcons } from './dashboard-category-icons';
import './Dashboard.css';
import { FlightStatsDetailsDialog } from './FlightStatsDetailsDialog';

type MonitorStatusName = 'emergency' | 'critical' | 'warning' | 'watch' | 'info' | 'clear';
const MONITOR_STATUS_ORDER: MonitorStatusName[] = ['emergency', 'critical', 'warning', 'watch', 'info', 'clear'];
const MONITOR_STATUS_CONFIG: Record<MonitorStatusName, { label: string; value: MonitorStatus }> = {
    emergency: { label: 'Emergency', value: MonitorStatus.emergency },
    critical: { label: 'Critical', value: MonitorStatus.critical },
    warning: { label: 'Warning', value: MonitorStatus.warning },
    watch: { label: 'Watch', value: MonitorStatus.watch },
    info: { label: 'Info', value: MonitorStatus.info },
    clear: { label: 'Clear', value: MonitorStatus.clear }
};
export type StatusFilter = FlightStatus.Active | FlightStatus.Planned | FlightStatus.Scheduled;
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: FlightStatus.Active, label: 'Active' },
    { value: FlightStatus.Planned, label: 'Planned' },
    { value: FlightStatus.Scheduled, label: 'Scheduled' }
];
const columns = [UDViewTagSelectionEnum.MonitorStatus];
type ChartFlight = Record<number | 'id', unknown>;
const isSameFlight = (first: ChartFlight, second: ChartFlight) =>
    first.id === second.id && columns.every((column) => first[column] === second[column]);

export function FlightStatsChart() {
    const theme = useTheme();
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const lastUpdate = useAppSelector((state) => state.global.flightWorker.lastUpdate?.time);
    const [flightStatus, setFlightStatus] = useState<StatusFilter>(FlightStatus.Active);
    const [data, setData] = useState<ChartFlight[]>([]);
    const [selectedMonitorStatus, setSelectedMonitorStatus] = useState<MonitorStatusName | null>(null);

    useEffect(() => {
        let cancelled = false;
        queryFlightsGrid({
            columns,
            state: {
                filter: {
                    logic: 'and',
                    filters: [
                        { field: 'status', operator: 'eq', value: flightStatus },
                        { field: 'monitorStatus', operator: 'neq', value: MonitorStatus.null }
                    ]
                },
                sort: [{ field: 'id', dir: 'asc' }],
                skip: 0,
                take: Number.MAX_SAFE_INTEGER
            },
            filterOrSortChanged: true,
            previousResult: undefined,
            previousSkip: 0,
            filterMode: FlightListFilterMode.myFleet,
            focusedWorkspaceId
        })
            .then((result) => {
                if (cancelled) return;
                setData(result.result.data);
            })
            .catch((error) => {
                if (!cancelled) console.error('Unable to refresh flight statistics', error);
            });
        return () => {
            cancelled = true;
        };
    }, [focusedWorkspaceId, flightStatus, lastUpdate]);

    const { series, total } = useMemo(() => {
        const counts = new Map<MonitorStatusName, number>(MONITOR_STATUS_ORDER.map((status) => [status, 0]));
        for (const flight of data) {
            const status = flight[UDViewTagSelectionEnum.MonitorStatus] as MonitorStatusName;
            if (counts.has(status)) counts.set(status, counts.get(status)! + 1);
        }
        const series = MONITOR_STATUS_ORDER.filter((status) => counts.get(status)).map((status) => ({
            id: status,
            label: MONITOR_STATUS_CONFIG[status].label,
            value: counts.get(status)!,
            color: status === 'clear' ? theme.palette.text.secondary : getMonitorStatusOutlineColorHex(MONITOR_STATUS_CONFIG[status].value)
        }));
        return { series, total: series.reduce((sum, item) => sum + item.value, 0) };
    }, [data, theme.palette.text.secondary]);

    return (
        <section className="dashboard-card dashboard-flight-panel flight-stats-chart dashboard-flight-panel--monitored">
            <header className="dashboard-flight-panel__header">
                <div className="dashboard-flight-panel__title">
                    <span className="dashboard-summary__icon" aria-hidden="true">
                        <Icon component={dashboardCategoryIcons.monitored} />
                    </span>
                    <h2 className="dashboard-flight-panel__title-text">Flights by Monitor Status</h2>
                    <span className="dashboard-flight-panel__count" aria-label={`${total} monitored flights`}>
                        {total.toLocaleString()}
                    </span>
                </div>
            </header>
            <Box className="dashboard-flight-panel__content" sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
                <ToggleButtonGroup
                    size="small"
                    className="flight-stats-chart-toggle-group"
                    exclusive
                    value={flightStatus}
                    onChange={(_, value: StatusFilter | null) => value !== null && setFlightStatus(value)}
                    aria-label="Flight status"
                    sx={{ alignSelf: 'center' }}>
                    {STATUS_OPTIONS.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {total === 0 ? (
                    <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <div className="dashboard-flight-grid__empty">
                            <span className="dashboard-flight-grid__empty-icon" aria-hidden="true">
                                <Icon component={dashboardCategoryIcons.monitored} />
                            </span>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>No flight data available</span>
                        </div>
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, minHeight: 200, margin: 3 }}>
                        <PieChart
                            series={[
                                {
                                    data: series,
                                    arcLabel: ({ value }) => `${value}`,
                                    arcLabelMinAngle: 35,
                                    innerRadius: '50%',
                                    outerRadius: '100%',
                                    paddingAngle: 1,
                                    cornerRadius: 3,
                                    highlightScope: { fade: 'global', highlight: 'item' },
                                    valueFormatter: ({ value }) => `${value} flight${value === 1 ? '' : 's'}`
                                }
                            ]}
                            slotProps={{ legend: { direction: 'vertical' } }}
                            onItemClick={(_, item) => {
                                setSelectedMonitorStatus(series[item.dataIndex].id);
                            }}
                        />
                    </Box>
                )}
            </Box>
            {selectedMonitorStatus && (
                <FlightStatsDetailsDialog
                    key={`${selectedMonitorStatus}-${flightStatus}-${focusedWorkspaceId}`}
                    monitorStatus={MONITOR_STATUS_CONFIG[selectedMonitorStatus].value}
                    monitorStatusLabel={MONITOR_STATUS_CONFIG[selectedMonitorStatus].label}
                    flightStatus={flightStatus}
                    flightStatusLabel={STATUS_OPTIONS.find((option) => option.value === flightStatus)!.label}
                    onClose={() => setSelectedMonitorStatus(null)}
                />
            )}
        </section>
    );
}
